/**
 * The expiring contract, as a read-only block (decision 127).
 *
 * Extracted from the Renewals slide-over so the BUILDER can show the same
 * picture while a renewal quote is being written — Mark, 2026-08-16: "when
 * preparing the new quote, it would be good to have the existing contract
 * lines to be available to view". One component, used in both places, so the
 * two can never drift into disagreeing about the same contract.
 *
 * ⚠️ Reference ONLY. The contract's lines are NOT pulled into the new quote:
 * older contracts carry prices and SKUs that frequently no longer exist, so a
 * renewal always prices from today's catalogue (Mark's standing rule, #105).
 * The heading says so on screen — do not quietly turn this into a copy button.
 *
 * Actions (create quote, link deal, record an outcome) deliberately live ONLY
 * on the Renewals page. This block never changes anything.
 */

import type React from 'react'
import { daysUntil, formatUKDate } from '@shared/dates'
import { formatGBP } from '@shared/money'
import { hubspotDealUrl, type ContractLine, type RenewalContract } from '../lib/api.js'

/** Renewal pipeline stages (9939314) — ids are settings, labels are ours. */
export const STAGE_LABELS: Record<string, string> = {
  '10765027': 'Live 2yrs+', '10765028': 'Live 1–2yrs', '10765026': 'Final year',
  '11084429': 'Due 41–97d', '11084430': 'Due <40d', '11084431': 'Due <7d',
  '1081973843': 'At risk', '1413674026': 'Process rollover',
  '45239427': 'New contract in PRJ', '11084432': 'New contract completed',
  '9939321': 'Lost — cancel services', '9939348': 'Lost — cancelled',
  '11012509': 'Contract import',
}

export const OUTCOME_PILL: Record<string, { label: string; cls: string }> = {
  open: { label: 'open', cls: 'pill pill--info' },
  renewed: { label: 'renewed', cls: 'pill pill--ok' },
  rolled_over: { label: 'rolled over', cls: 'pill pill--ok' },
  lost: { label: 'not renewing', cls: 'pill pill--stop' },
}

/** Both callers already hold everything this block shows. */
export function ContractFacts({ r, portalId, extraFacts }: {
  r: RenewalContract
  portalId: string | null
  /** Caller-specific rows (the Renewals page adds its "Renewal quote" link). */
  extraFacts?: React.ReactNode
}) {
  const dealLink = hubspotDealUrl(portalId, r.hsDealId)
  const d = r.contractEndDate ? daysUntil(r.contractEndDate) : null

  const fact = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}>
      <span className="muted small">{label}</span>
      <span className="small" style={{ textAlign: 'right' }}>{value}</span>
    </div>
  )

  return (
    <>
      {fact('Outcome', <span className={(OUTCOME_PILL[r.outcome] ?? OUTCOME_PILL.open).cls}>
        {(OUTCOME_PILL[r.outcome] ?? OUTCOME_PILL.open).label}
      </span>)}
      {r.outcome !== 'open' && r.outcomeNote && fact('Note', r.outcomeNote)}
      {r.outcome !== 'open' && r.outcomeActor && fact('Recorded by', `${r.outcomeActor}${r.outcomeAt ? ` · ${formatUKDate(r.outcomeAt)}` : ''}`)}
      {r.outcome !== 'open' && !r.hsMoveDone && fact('HubSpot', (
        <span className="pill pill--warn tiny" title={r.hsLastError ?? undefined}>stage move pending — the sweep retries</span>
      ))}
      {fact('Contract ends', r.contractEndDate
        ? <>{formatUKDate(r.contractEndDate)}{d != null && <span className="muted"> · {d < 0 ? `${-d}d overdue` : `${d}d left`}</span>}</>
        : '—')}
      {r.contractStartDate && fact('Started', formatUKDate(r.contractStartDate))}
      {fact('Stage', r.dealStage ? (STAGE_LABELS[r.dealStage] ?? r.dealStage) : '—')}
      {r.dealAmount != null && fact('Deal value', formatGBP(r.dealAmount))}
      {r.hsArr != null && fact('Contract ARR', formatGBP(r.hsArr))}
      {r.ownerName && fact('Deal owner', r.ownerName)}
      {fact('Contract deal', dealLink
        ? <a href={dealLink} target="_blank" rel="noreferrer">{r.hsDealId} ↗</a>
        : r.hsDealId)}
      {r.quotientLink && fact('Original quote', /^https?:\/\//i.test(r.quotientLink)
        ? <a href={r.quotientLink} target="_blank" rel="noreferrer"
            title="The deal's Quotient Link — the quote this contract was originally sold on.">Open original quote ↗</a>
        : <span title="The deal's Quotient Link — a reference rather than a URL.">{r.quotientLink}</span>)}
      {extraFacts}
      {r.newDealId && fact('Linked new deal', hubspotDealUrl(portalId, r.newDealId)
        ? <a href={hubspotDealUrl(portalId, r.newDealId)!} target="_blank" rel="noreferrer">{r.newDealId} ↗</a>
        : r.newDealId)}
      {r.lastSyncedAt && fact('Last mirrored', formatUKDate(r.lastSyncedAt))}
    </>
  )
}

/**
 * What the customer is on TODAY. The subheading is load-bearing: it is the
 * only thing telling the reader these prices are historic, not a quote.
 */
export function ContractLines({ lines }: { lines: ContractLine[] | null }) {
  if (!lines || lines.length === 0) return null
  return (
    <div style={{ marginTop: 14 }}>
      <h3 className="small" style={{ margin: '0 0 6px' }}>
        Current contract lines{' '}
        <span className="muted">(reference — the new quote prices from today's catalogue)</span>
      </h3>
      {lines.map((li, i) => (
        <div key={i} className="small" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0', borderBottom: '1px solid var(--line-soft)' }}>
          <span>
            {li.name}
            {li.term ? <span className="tiny muted"> · {li.term.replace('P', '').replace('M', ' mo')}</span> : null}
          </span>
          <span className="muted" style={{ whiteSpace: 'nowrap' }}>
            {li.qty != null ? `${li.qty} × ` : ''}{li.price != null ? formatGBP(li.price) : '—'}
          </span>
        </div>
      ))}
    </div>
  )
}
